import type { LocationPrecision } from '@attik/types/property.types';

/** The fields of a Mapbox place suggestion the address search reads. */
export type AddressSuggestionLike = {
  place_name: string;
  matching_place_name?: string;
  /** House number; absent when Mapbox matched only the street. */
  address?: string;
};

/** The text a suggestion is listed under, which is also its Autocomplete key. */
export const suggestionLabel = (suggestion: AddressSuggestionLike): string =>
  suggestion.matching_place_name || suggestion.place_name;

/**
 * The suggestion the person clicked, found by the key it was listed under.
 *
 * Compares the whole label. The old `x.matching_place_name || x.place_name === item`
 * parsed as `matching_place_name || (place_name === item)`, so the first
 * suggestion with any partial-match text won whichever one was clicked, and its
 * street, city, zip and pin were saved instead.
 */
export function findSelectedSuggestion<T extends AddressSuggestionLike>(
  suggestions: T[],
  key: string,
): T | undefined {
  return suggestions.find((suggestion) => suggestionLabel(suggestion) === key);
}

/**
 * How exactly a picked suggestion pins the building. A suggestion without a
 * house number is the street as a whole, not the property on it.
 */
export const suggestionPrecision = (
  suggestion: AddressSuggestionLike,
): LocationPrecision => (suggestion.address ? 'address' : 'approximate');

type PropertyAddressParts = {
  street: string;
  city: string;
  state: string;
  zip: string;
};

type Coordinates = { lat: number; lng: number };

/**
 * One geocode attempt: the query, whether to search postcodes instead of
 * addresses, and whether only a match with a house number counts.
 */
export type GeocodeAttempt = (
  query: string,
  options?: { types?: 'postcode'; requireHouseNumber?: boolean },
) => Promise<Coordinates | null>;

/**
 * Geocodes a typed address, coarsest last, and says how exact the result is.
 *
 * 1. The full address, matched to a house number — a street-level match. A
 *    match on the street alone (Mapbox's answer to an unknown house number)
 *    falls through to the approximate tier.
 * 2. City, state and zip — still an address search, so Mapbox answers with some
 *    address in that zip: plausible-looking, but not the property.
 * 3. The zip alone — its centre point.
 *
 * Returns `null` when nothing resolves.
 */
export async function geocodePropertyAddress(
  parts: PropertyAddressParts,
  geocode: GeocodeAttempt,
): Promise<(Coordinates & { locationPrecision: LocationPrecision }) | null> {
  const full = await geocode(
    `${parts.street}, ${parts.city}, ${parts.state} ${parts.zip}`,
    { requireHouseNumber: true },
  );
  if (full) return { ...full, locationPrecision: 'address' };

  const cityZip = await geocode(`${parts.city}, ${parts.state} ${parts.zip}`);
  if (cityZip) return { ...cityZip, locationPrecision: 'approximate' };

  if (!parts.zip) return null;
  const zip = await geocode(parts.zip, { types: 'postcode' });
  if (zip) return { ...zip, locationPrecision: 'postcode' };

  return null;
}
